import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Play, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { createApiUrl } from '../services/shareLinkService';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const STATUS_STYLES = {
  sent: 'bg-green-100 text-green-800',
  dry_run: 'bg-blue-100 text-blue-800',
  skipped: 'bg-gray-100 text-gray-700',
  failed: 'bg-red-100 text-red-800',
};
const STATUS_LABELS = { sent: 'Gesendet', dry_run: 'Simuliert', skipped: 'Übersprungen', failed: 'Fehler' };
const TYPE_LABELS = { on_the_way: 'Unterwegs', delay: 'Verspätung' };
const CONFIG_LABELS = { stressfrei: 'Stressfrei', traccar: 'Traccar', pipedrive: 'Pipedrive', google: 'Google Maps (Server-Key)', twilio: 'Twilio' };
const PLACEHOLDERS = '{anrede} {firma} {von} {bis} {link}';

/** Admin: Kunden-SMS (Dry-Run/Live), Vorlagen und Protokoll des Tracking-Jobs. */
const TrackingAdminTab = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(createApiUrl('api/admin/tracking/status'), { headers: authHeaders() });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const body = await res.json();
      setData(body);
      setDraft(prev => prev || body.settings);
    } catch (err) {
      setMessage({ type: 'error', text: `Status konnte nicht geladen werden: ${err.message}` });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (updates) => {
    setBusy(true);
    try {
      const res = await fetch(createApiUrl('api/admin/tracking/settings'), { method: 'PUT', headers: authHeaders(), body: JSON.stringify(updates) });
      if (!res.ok) throw new Error(res.status === 403 ? 'Nur Administratoren dürfen das ändern' : `Status ${res.status}`);
      const { settings } = await res.json();
      setDraft(settings);
      setMessage({ type: 'success', text: 'Gespeichert' });
      await load();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const toggleLive = () => {
    const goLive = data.settings.sms_live !== 'true';
    if (goLive && !window.confirm('SMS ab jetzt wirklich an Kunden senden? Bisher wurden sie nur simuliert.')) return;
    save({ sms_live: goLive ? 'true' : 'false' });
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const res = await fetch(createApiUrl('api/admin/tracking/run'), { method: 'POST', headers: authHeaders() });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      await load();
      setMessage({ type: 'success', text: 'Prüfung durchgeführt' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (!data || !draft) {
    return <div className="text-sm text-gray-500">{message?.text || 'Lade Tracking-Status…'}</div>;
  }

  const live = data.settings.sms_live === 'true';
  const result = data.lastResult;

  return (
    <div className="space-y-6">
      {message && (
        <div className={`text-sm px-3 py-2 rounded ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{message.text}</div>
      )}

      <section className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-lg border border-gray-200">
        <div>
          <h3 className="font-semibold text-gray-900">Kunden-SMS</h3>
          <p className="text-sm text-gray-600">
            {live ? 'Live: SMS werden an Kunden gesendet.' : 'Dry-Run: SMS werden nur protokolliert, nicht gesendet.'}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={runNow} disabled={busy} className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1.5 disabled:opacity-50">
            <Play size={15} /> Jetzt prüfen
          </button>
          <button onClick={toggleLive} disabled={busy} className={`px-3 py-2 text-sm rounded-lg text-white disabled:opacity-50 ${live ? 'bg-gray-700 hover:bg-gray-800' : 'bg-green-600 hover:bg-green-700'}`}>
            {live ? 'Zurück auf Dry-Run' : 'Live schalten'}
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Verbindungen</h4>
          <ul className="space-y-1 text-sm">
            {Object.entries(data.configured).map(([key, ok]) => (
              <li key={key} className="flex items-center gap-2">
                {ok ? <CheckCircle2 size={15} className="text-green-600" /> : <XCircle size={15} className="text-red-500" />}
                {CONFIG_LABELS[key] || key}
              </li>
            ))}
          </ul>
        </div>
        <div className="p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-700">Letzter Lauf</h4>
            <button onClick={load} className="text-gray-500 hover:text-gray-800" aria-label="Aktualisieren"><RefreshCw size={14} /></button>
          </div>
          {result ? (
            <div className="text-sm text-gray-700 space-y-1">
              <p>{new Date(result.finishedAt || result.startedAt).toLocaleString('de-DE')} · {result.vehicles || 0} Fahrzeuge · {result.actions?.length || 0} Aktionen</p>
              {result.skipped && <p className="text-gray-500">{result.skipped}</p>}
              {result.error && <p className="text-red-600">Fehler: {result.error}</p>}
              {(result.warnings || []).map((w, i) => (
                <p key={i} className="flex gap-1.5 text-amber-700"><AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />{w}</p>
              ))}
            </div>
          ) : <p className="text-sm text-gray-500">Noch kein Lauf seit dem Serverstart.</p>}
        </div>
      </section>

      <section className="p-4 rounded-lg border border-gray-200 space-y-3">
        <h4 className="text-sm font-semibold text-gray-700">Vorlagen</h4>
        <p className="text-xs text-gray-500">Platzhalter: {PLACEHOLDERS}. Bis ca. 160 Zeichen = 1 SMS; Gedankenstriche und Emojis vermeiden (verkürzen die SMS auf 70 Zeichen).</p>
        {[
          ['template_on_the_way', 'Unterwegs (nach "Tats. Ende")'],
          ['template_delay', 'Verspätung'],
        ].map(([key, label]) => (
          <label key={key} className="block">
            <span className="text-sm text-gray-700">{label}</span>
            <textarea
              value={draft[key]}
              onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
              rows={3}
              className="mt-1 w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </label>
        ))}
        <div className="flex flex-wrap gap-4 items-end">
          <label className="text-sm text-gray-700">Firmenname
            <input value={draft.company_name} onChange={e => setDraft(d => ({ ...d, company_name: e.target.value }))} className="ml-2 p-1.5 border border-gray-300 rounded-lg" />
          </label>
          <label className="text-sm text-gray-700">Aktiv von
            <input type="number" min="0" max="23" value={draft.active_from_hour} onChange={e => setDraft(d => ({ ...d, active_from_hour: e.target.value }))} className="mx-2 w-16 p-1.5 border border-gray-300 rounded-lg" />
            bis
            <input type="number" min="1" max="24" value={draft.active_to_hour} onChange={e => setDraft(d => ({ ...d, active_to_hour: e.target.value }))} className="ml-2 w-16 p-1.5 border border-gray-300 rounded-lg" /> Uhr
          </label>
          <button
            onClick={() => save({
              template_on_the_way: draft.template_on_the_way,
              template_delay: draft.template_delay,
              company_name: draft.company_name,
              active_from_hour: draft.active_from_hour,
              active_to_hour: draft.active_to_hour,
            })}
            disabled={busy}
            className="px-3 py-2 text-sm rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            Speichern
          </button>
        </div>
      </section>

      <section>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Protokoll</h4>
        {data.notifications.length === 0 ? (
          <p className="text-sm text-gray-500">Noch keine SMS ausgelöst.</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500">
                <tr>
                  <th className="px-3 py-2 font-normal">Zeit</th>
                  <th className="px-3 py-2 font-normal">Art</th>
                  <th className="px-3 py-2 font-normal">Status</th>
                  <th className="px-3 py-2 font-normal">Vorgang</th>
                  <th className="px-3 py-2 font-normal">Empfänger</th>
                  <th className="px-3 py-2 font-normal">Nachricht</th>
                </tr>
              </thead>
              <tbody>
                {data.notifications.map(n => (
                  <tr key={n.id} className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(`${n.updated_at.replace(' ', 'T')}Z`).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="px-3 py-2">{TYPE_LABELS[n.type] || n.type}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[n.status] || ''}`}>{STATUS_LABELS[n.status] || n.status}</span>
                      {n.error && <div className="text-xs text-gray-500 mt-1">{n.error}</div>}
                    </td>
                    <td className="px-3 py-2">{n.vorgangsnummer}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{n.customer_name || '–'}<div className="text-xs text-gray-500">{n.phone || ''}</div></td>
                    <td className="px-3 py-2 text-gray-700 min-w-[16rem]">{n.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default TrackingAdminTab;
